import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { EditAgeGroupSheet } from "./edit-age-group-sheet";
import { mockAgeGroups, type AgeGroup } from "../mock-data";

export function AgeGroupsSection() {
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>(mockAgeGroups);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AgeGroup | null>(null);

  const handleAddClick = () => {
    setEditingGroup(null);
    setIsSheetOpen(true);
  };

  const handleEditClick = (group: AgeGroup) => {
    setEditingGroup(group);
    setIsSheetOpen(true);
  };

  const handleSaveGroup = (savedGroup: AgeGroup) => {
    if (editingGroup) {
      // Update existing group
      setAgeGroups(
        ageGroups.map((g) => (g.id === savedGroup.id ? savedGroup : g))
      );
    } else {
      // Add new group
      setAgeGroups([...ageGroups, savedGroup]);
    }
  };

  const handleDeleteGroup = (id: string) => {
    setAgeGroups(ageGroups.filter((g) => g.id !== id));
    toast.success("Groupe supprimé");
  };

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-gray-700">Groupes d'âge</h3>

      <div className="space-y-2">
        {ageGroups.map((group) => (
          <div
            key={group.id}
            className="flex items-center justify-between rounded-lg border bg-white p-3"
          >
            <div>
              <div className="font-medium text-gray-900">{group.name}</div>
              <div className="text-sm text-gray-500">
                Enfants de {group.minAge} à {group.maxAge} ans
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-500 hover:text-gray-700"
                onClick={() => handleEditClick(group)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer le groupe</AlertDialogTitle>
                    <AlertDialogDescription>
                      Êtes-vous sûr de vouloir supprimer le groupe "{group.name}" ?
                      Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDeleteGroup(group.id)}>
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}

        {/* Add button */}
        <button
          onClick={handleAddClick}
          className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 p-3 text-sm text-orange-600 transition-colors hover:border-orange-300 hover:bg-orange-50"
        >
          <Plus className="h-4 w-4" />
          <span>Ajouter un groupe d'âge</span>
        </button>
      </div>

      {/* Edit/Add sheet */}
      <EditAgeGroupSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        group={editingGroup}
        existingGroups={ageGroups}
        onSave={handleSaveGroup}
      />
    </div>
  );
}
